import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { VisitCookieService } from '../public/visit-cookie.service';
import { LocationService } from '../public/location.service';
import { InactivityModalComponent } from '../public/store/inactivity-modal/inactivity-modal.component';
import { CustomerDataService } from './customerDataService';
import { Store } from '@ngrx/store';
import {
  ClearOrderMeta,
  ClearStoreLocation,
  InitializeOrder,
  LoadCatalog,
  UpdateLocation,
  ViewStateUpdate,
} from '../public/store/+state/stores.actions';
import { ClientStore, OrderItem } from '../stores/stores';
import { OrderService } from '../public/order.service';
import { BehaviorSubject, filter, Subject, take, takeUntil } from 'rxjs';
import { CheckoutService } from '../public/store/store-checkout/checkout.service';
import { HelperService } from '../public/helper.service';
import {
  getCurrentCartContent,
  getSelectedLang,
} from '../public/store/+state/stores.selectors';
import { InitialViewOrchestratorService } from '../public/store/services/initial-view-orchestrator.service';

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {
  private userActivity: ReturnType<typeof setTimeout> | null = null;
  private modalShown: boolean = false;
  private inActivityTimer: number = 120000;
  private modalActionTimeout: ReturnType<typeof setTimeout> | null = null;
  private selectedStoreForInactivity: ClientStore | null = null;
  private hasScreenMoved: boolean = false;
  private hasBasketItems: boolean = false;
  private isKioskVideoPlaying: boolean = false;
  private trackedStoreAliasName: string | null = null;
  private globalListenersInitialized: boolean = false;
  private destroy$ = new Subject<void>();
  private lastInteractionHandledAt = 0;
  private readonly interactionThrottleMs = 250;
  private ignoreNextNavigationAsActivity = false;

  private inactivityStatus = new BehaviorSubject<
    'SHOW_KIOSK_VIDEO' | 'HIDE_KIOSK_VIDEO'
  >('HIDE_KIOSK_VIDEO');
  inactivityStatus$ = this.inactivityStatus.asObservable();

  private readonly handleGlobalInteraction = (): void => {
    if (!this.modalShown || !this.selectedStoreForInactivity) {
      return;
    }
    const now = Date.now();
    if (now - this.lastInteractionHandledAt < this.interactionThrottleMs) {
      return;
    }
    this.lastInteractionHandledAt = now;
    this.refreshUserState(this.selectedStoreForInactivity, true);
  };

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private customerDataService: CustomerDataService,
    private store: Store,
    private orderService: OrderService,
    private checkoutService: CheckoutService,
    private helper: HelperService,
    private visitCookies: VisitCookieService,
    private locationService: LocationService,
    private initialViewOrchestrator: InitialViewOrchestratorService,
  ) {
    this.watchCartContent();
    this.registerGlobalInteractionListeners();
    this.registerNavigationActivityListener();
  }

  private registerNavigationActivityListener(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (!this.modalShown || !this.selectedStoreForInactivity) {
          return;
        }
        const shouldMarkScreenMoved = !this.ignoreNextNavigationAsActivity;
        this.ignoreNextNavigationAsActivity = false;
        this.refreshUserState(
          this.selectedStoreForInactivity,
          shouldMarkScreenMoved,
        );
      });
  }

  public setTimeout(selectedStore: ClientStore): void {
    if (this.locationService.isAdminOrderCaptureUpdate()) {
      return;
    }
    if (!selectedStore?.settings) {
      return;
    }

    this.selectedStoreForInactivity = selectedStore;

    if (this.trackedStoreAliasName !== (selectedStore.aliasName ?? null)) {
      this.trackedStoreAliasName = selectedStore.aliasName ?? null;
      this.setScreenMoved(false);
    }
    if (!selectedStore?.settings?.INACTIVITY_OPTION) {
      return;
    }

    if (selectedStore.settings['INACTIVITY_SESSION_TIMEOUT_MINUTES'] > 0) {
      this.inActivityTimer =
        selectedStore.settings['INACTIVITY_SESSION_TIMEOUT_MINUTES'] *
        60 *
        1000;
    }

    this.clearInactivityTimer();

    if (!this.shouldTriggerInactivityTimer(selectedStore)) {
      return;
    }
    this.userActivity = setTimeout(
      () => this.openModal(selectedStore),
      this.inActivityTimer,
    );
  }

  public refreshUserState(
    selectedStore: ClientStore,
    markScreenMoved: boolean = true,
  ): void {
    if (this.locationService.isAdminOrderCaptureUpdate()) {
      return;
    }
    if (!selectedStore?.settings?.INACTIVITY_OPTION) {
      return;
    }

    this.selectedStoreForInactivity = selectedStore;
    if (markScreenMoved) {
      this.setScreenMoved(true);
    }

    this.clearInactivityTimer();
    if (this.modalShown) {
      this.setTimeout(selectedStore);
    }
  }

  /**
   * Mark the user as active from a source outside the host DOM — used by
   * the AI Ordering Assistant iframe (see AiWidgetBridgeService), whose
   * keystroke / send / microphone events do not bubble across the
   * cross-document boundary and therefore never reach the
   * `handleGlobalInteraction` listener on `window`/`document`.
   *
   * Behaves like a regular host-side interaction would:
   *   - early-return on Admin Order Capture (matches the guards in
   *     `setTimeout` and `refreshUserState`; the inactivity flow is
   *     suppressed there);
   *   - honor the shared `interactionThrottleMs` so a fast typist or a
   *     long voice monologue cannot saturate the timer-reset path;
   *   - when the modal is up, reset its auto-close countdown via the
   *     existing `refreshUserState` path;
   *   - when the modal is NOT up, re-arm the pre-modal `setTimeout`
   *     countdown so the modal does not pop while the user is actively
   *     engaged with the chat. This is the actual fix for card #9685
   *     since `handleGlobalInteraction` only resets when the modal is
   *     already shown.
   */
  public notifyExternalActivity(): void {
    if (this.locationService.isAdminOrderCaptureUpdate()) {
      return;
    }
    if (!this.selectedStoreForInactivity) {
      return;
    }
    const now = Date.now();
    if (now - this.lastInteractionHandledAt < this.interactionThrottleMs) {
      return;
    }
    this.lastInteractionHandledAt = now;
    if (this.modalShown) {
      this.refreshUserState(this.selectedStoreForInactivity, true);
    } else {
      this.setTimeout(this.selectedStoreForInactivity);
    }
  }

  public navigateToOrderCapture() {
    this.ignoreNextNavigationAsActivity = true;
    this.router.navigate([], {
      fragment: null,
      relativeTo: null,
      replaceUrl: true,
      state: { clearCart: true },
    });
  }

  public reloadFunction(): void {
    location.reload();
  }

  public openModal(selectedStore: ClientStore): void {
    if (this.modalShown) {
      const dialogRef = this.dialog.open(InactivityModalComponent, {
        width: '490px',
        panelClass: 'cpanel-activity',
      });

      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((value) => {
          if (value === 'START_NEW_ORDER' || value === 'TIMER_END') {
            this.resetOrderAndStorefront(selectedStore, value);
          }
        });
    }
  }

  /**
   * Public entry point reused by `OrderUpdateBlockedDialogComponent` so the
   * "Start a new order" button on the payment-in-progress dialog clears the
   * basket / identity / location with the exact same plumbing as the inactivity
   * timer's Start-new-order branch. Kiosk video playback stays gated on
   * `TIMER_END` only — clicking Start-new-order from the blocked dialog must
   * not play the kiosk attract video.
   */
  public startNewOrderFromBlockedDialog(selectedStore: ClientStore): void {
    this.resetOrderAndStorefront(selectedStore, 'BLOCKED_DIALOG');
  }

  private resetOrderAndStorefront(
    selectedStore: ClientStore,
    mode: 'START_NEW_ORDER' | 'TIMER_END' | 'BLOCKED_DIALOG',
  ): void {
    this.closeOpenDialogsForInactivityTransition();
    this.clearOrderIdentity(selectedStore);
    this.navigateToOrderCapture();

    this.modalActionTimeout = setTimeout(() => {
      if (mode === 'TIMER_END') {
        this.initialViewOrchestrator.clearAllState(selectedStore.aliasName);
      } else {
        // START_NEW_ORDER + BLOCKED_DIALOG share the same intent: clear the
        // consumption-mode answer so the user is asked again, and flag the next
        // session as a reorder.
        this.initialViewOrchestrator.clearConsumptionModeState(
          selectedStore.aliasName,
        );
        this.initialViewOrchestrator.setReorderFlow(
          selectedStore.aliasName,
          true,
        );
      }

      this.store.dispatch(new ClearOrderMeta({}));

      this.store
        .select(getSelectedLang)
        .pipe(take(1))
        .subscribe((currentLang) => {
          this.store.dispatch(
            new InitializeOrder(
              selectedStore.id,
              {
                ...this.orderService.getInitOrder(),
                // Omit the field entirely when no language is selected yet so
                // the server keeps its default instead of receiving null.
                ...(currentLang ? { catalogLanguageLocale: currentLang } : {}),
              },
              {
                src:
                  mode === 'BLOCKED_DIALOG'
                    ? 'OrderUpdateBlockedDialog'
                    : 'InactivityService',
                description:
                  mode === 'TIMER_END'
                    ? 'Inactivity timeout'
                    : mode === 'START_NEW_ORDER'
                      ? 'Start new order'
                      : 'Start new order from blocked dialog',
              },
            ),
          );
          this.dispatchCatalogReload(selectedStore.id);
        });
      this.customerDataService.cleanCustomerData();

      if (selectedStore.settings['INACTIVITY_CLEAR_LOCATION'] === true) {
        this.locationService.removeLocationUrlType(selectedStore.aliasName);
        this.locationService.removeLocationLabel(selectedStore.aliasName);
        this.checkoutService.clearValidLocations();
        this.locationService.removeLocationOrder(selectedStore.aliasName);
        this.store.dispatch(new ClearStoreLocation());
        this.store.dispatch(
          new UpdateLocation(null, this.helper.getDeviceID()),
        );
      }

      this.setScreenMoved(false);

      if (mode === 'TIMER_END' && selectedStore.settings.INACTIVITY_VIDEO) {
        this.setInactivityStatus('SHOW_KIOSK_VIDEO');
      }

      this.modalActionTimeout = null;
    }, 500);
  }

  public setModalShown(value: boolean): void {
    this.modalShown = value;
    if (!value) {
      this.clearInactivityTimer();
      this.selectedStoreForInactivity = null;
    }
  }

  public getModalShown(): boolean {
    return this.modalShown;
  }

  setInactivityStatus(value: 'SHOW_KIOSK_VIDEO' | 'HIDE_KIOSK_VIDEO') {
    this.isKioskVideoPlaying = value === 'SHOW_KIOSK_VIDEO';
    this.inactivityStatus.next(value);

    if (value === 'SHOW_KIOSK_VIDEO') {
      this.clearInactivityTimer();
      return;
    }

    this.setScreenMoved(false);
    if (this.selectedStoreForInactivity && this.modalShown) {
      this.setTimeout(this.selectedStoreForInactivity);
    }
  }

  public cleanup(): void {
    this.clearInactivityTimer();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unregisterGlobalInteractionListeners();
    this.clearInactivityTimer();
  }

  // Re-fetches the catalog so stock/availability changes that happened during
  // the previous session are reflected (e.g. items marked out of stock are
  // greyed out instead of remaining as if they were still in stock).
  private dispatchCatalogReload(storeId: number): void {
    this.store
      .select(getSelectedLang)
      .pipe(take(1))
      .subscribe((lang) => {
        this.store.dispatch(new LoadCatalog(storeId, lang, null));
      });
  }

  private clearOrderIdentity(selectedStore: ClientStore): void {
    const aliasName = selectedStore?.aliasName;
    if (aliasName) {
      // Card #11603 - through VisitCookieService, so the copy kept when the
      // browser refused the cookie goes too.
      this.visitCookies.delete(`orderUuid-${aliasName}`, '/');
      this.visitCookies.delete(`orderUuid-admin-${aliasName}`, '/');
    }
    this.locationService.setOrderUuid('');
  }

  private closeOpenDialogsForInactivityTransition(): void {
    this.dialog.closeAll();
  }

  private watchCartContent(): void {
    this.store
      .select(getCurrentCartContent)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cartItems: OrderItem[]) => {
        const previousHasBasketItems = this.hasBasketItems;
        this.hasBasketItems = (cartItems?.length ?? 0) > 0;

        if (
          !previousHasBasketItems &&
          this.hasBasketItems &&
          this.selectedStoreForInactivity &&
          this.modalShown
        ) {
          this.refreshUserState(this.selectedStoreForInactivity, true);
        }
      });
  }

  private registerGlobalInteractionListeners(): void {
    if (
      this.globalListenersInitialized ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    this.globalListenersInitialized = true;
    window.addEventListener('click', this.handleGlobalInteraction);
    window.addEventListener('scroll', this.handleGlobalInteraction, {
      passive: true,
    });
    window.addEventListener('touchstart', this.handleGlobalInteraction, {
      passive: true,
    });
    document.addEventListener('scroll', this.handleGlobalInteraction, {
      passive: true,
      capture: true,
    });
    document.addEventListener('touchstart', this.handleGlobalInteraction, {
      passive: true,
      capture: true,
    });
  }

  private unregisterGlobalInteractionListeners(): void {
    if (
      !this.globalListenersInitialized ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    window.removeEventListener('click', this.handleGlobalInteraction);
    window.removeEventListener('scroll', this.handleGlobalInteraction);
    window.removeEventListener('touchstart', this.handleGlobalInteraction);
    document.removeEventListener('scroll', this.handleGlobalInteraction, true);
    document.removeEventListener(
      'touchstart',
      this.handleGlobalInteraction,
      true,
    );
    this.globalListenersInitialized = false;
  }

  private shouldTriggerInactivityTimer(selectedStore: ClientStore): boolean {
    const currentView = this.locationService.getCurrentView();

    const hasVideo = !!selectedStore?.settings?.INACTIVITY_VIDEO;
    const hasConsumption =
      !selectedStore?.settings?.DELIVERY_ADDRESS &&
      selectedStore?.settings?.ORDER_CAPTURE_ASK_EAT_IN_TAKEAWAY;
    const hasPromotion = !!selectedStore?.promotion?.name;
    const hasAnyOrchestratedFeature =
      hasVideo || hasConsumption || hasPromotion;

    if (!hasAnyOrchestratedFeature) {
      const isInAnotherPage = this.isAnotherPageForInactivity(currentView);
      return this.hasScreenMoved || this.hasBasketItems || isInAnotherPage;
    }

    const isOnSpecialScreen =
      this.isKioskVideoPlaying ||
      currentView === 'kiosk-video' ||
      currentView === 'consumption-mode' ||
      currentView === 'promotion';

    if (isOnSpecialScreen) {
      return false;
    }

    return true;
  }

  private isAnotherPageForInactivity(
    currentView: string | null | undefined,
  ): boolean {
    const normalizedView = (currentView ?? '').trim().toLowerCase();
    if (!normalizedView) {
      return false;
    }
    return normalizedView !== 'category';
  }

  private setScreenMoved(value: boolean): void {
    this.hasScreenMoved = value;
  }

  private clearInactivityTimer(): void {
    if (this.userActivity !== null) {
      clearTimeout(this.userActivity);
      this.userActivity = null;
    }
  }
}
